import { useState } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { SITE } from "@/data/site";
import { FAQ_GROUPS } from "@/data/faqs";
import { INSIGHT_TOPICS } from "@/data/content";
import { PageHero, SectionHead, CtaBand, FaqList } from "@/components/Blocks";
import { Approach } from "@/components/home/Stories";
import VenturesBento from "@/components/home/VenturesBento";
import { Cta } from "@/components/Cta";
import { Reveal, Stagger, itemVariants } from "@/components/motion";
import { useSeo } from "@/lib/seo";

export function Work() {
  useSeo("Work", "How SparkIdea Interactive approaches product engineering, and the ventures we've built ourselves.");
  return (
    <>
      <PageHero testId="work-hero" eyebrow="Work" title={["Proof over promises."]}
        intro="We only publish case studies with real, approved information. Client case studies are being prepared with permission — in the meantime, here is how we structure our work and the products we've built ourselves." />
      <Approach />
      <section className="border-t border-si-line pt-20">
        <div className="wrap"><SectionHead eyebrow="Built by SparkIdea" title="Our own products." /></div>
      </section>
      <VenturesBento withHead={false} />
      <CtaBand testId="work-cta" title="Want to be our next story?" primary="Start a Project" />
    </>
  );
}

export function Insights() {
  useSeo("Insights", "Practical writing on startup product development, AI engineering, SaaS architecture, and technical leadership from SparkIdea Interactive.");
  const cats = ["All", ...new Set(INSIGHT_TOPICS.map(([c]) => c))];
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const list = INSIGHT_TOPICS.filter(([c, t]) => (cat === "All" || c === cat) && t.toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <PageHero testId="insights-hero" eyebrow="Insights" title={["Practical notes on", "building software products."]}
        intro="Our first articles are in preparation. Here's what we're writing about — useful, specific guidance for founders, not filler." />
      <section className="border-t border-si-line py-16 sm:py-24">
        <div className="wrap">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Categories">
              {cats.map((c) => (
                <button key={c} role="tab" aria-selected={cat === c} onClick={() => setCat(c)} data-testid={`insights-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${cat === c ? "border-si-cyan bg-si-cyan text-si-bg" : "border-si-line text-si-muted hover:text-si-text"}`}>{c}</button>
              ))}
            </div>
            <label className="relative block lg:w-80">
              <span className="sr-only">Search topics</span>
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-si-muted" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search topics" className="field pl-11" data-testid="insights-search" />
            </label>
          </div>
          <Stagger className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3" key={cat + q}>
            {list.map(([c, t]) => (
              <motion.article key={t} variants={itemVariants} className="panel flex min-h-[220px] flex-col p-7">
                <span className="eyebrow !text-si-muted">{c}</span>
                <h3 className="mt-5 text-xl font-bold leading-snug">{t}</h3>
                <span className="mt-auto pt-6 font-mono text-xs uppercase tracking-[0.18em] text-si-gold">In preparation</span>
              </motion.article>
            ))}
            {list.length === 0 && <p className="text-si-muted">No topics match your search.</p>}
          </Stagger>
          <Reveal className="mt-12 text-base text-si-muted">Want a topic covered? <a href={`mailto:${SITE.email}`} className="font-semibold text-si-cyan">Tell us at {SITE.email}</a>.</Reveal>
        </div>
      </section>
    </>
  );
}

export function FaqPage() {
  useSeo("FAQ", "Answers about SparkIdea Interactive's services, development process, pricing, AI work, venture partnerships, and international collaboration.");
  return (
    <>
      <PageHero testId="faq-hero" eyebrow="FAQ" title="Frequently asked questions." intro="Direct answers about how we work. Can't find yours? Just ask." />
      <section className="border-t border-si-line py-16 sm:py-24">
        <div className="wrap space-y-16">
          {FAQ_GROUPS.map((g, i) => (
            <div key={g.group} className="grid gap-8 lg:grid-cols-12">
              <div className="lg:col-span-4"><h2 className="text-2xl font-bold tracking-tight sm:text-3xl">{g.group}</h2></div>
              <div className="lg:col-span-8"><FaqList items={g.items} testId={`faq-group-${i}`} /></div>
            </div>
          ))}
        </div>
      </section>
      <CtaBand testId="faq-cta" title="Still have a question?" primary="Contact Us" secondary={null} />
    </>
  );
}

export function Careers() {
  useSeo("Careers & Collaborators", "Collaborate with SparkIdea Interactive. We don't have open roles right now, but we're glad to hear from talented people.");
  return (
    <>
      <PageHero testId="careers-hero" eyebrow="Careers & collaborators" title={["Builders welcome."]}
        intro="There are no open roles at the moment. We do occasionally work with independent collaborators — engineers, designers, and product specialists — and we're happy to hear from people who care about building great products." />
      <section className="border-t border-si-line py-16 sm:py-24">
        <div className="wrap max-w-3xl">
          <Reveal className="panel p-8 sm:p-10">
            <h2 className="text-2xl font-bold">Share your profile</h2>
            <p className="mt-4 text-base leading-relaxed text-si-muted">Send a short introduction, your portfolio or GitHub, and the kind of work you enjoy. We'll keep it on file and reach out if a relevant opportunity comes up.</p>
            <div className="mt-8"><Cta href={`mailto:${SITE.email}?subject=Collaboration%20introduction`} testId="careers-email">Email {SITE.email}</Cta></div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
